import { createStore, get, set, del } from 'idb-keyval'
import type {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client'
import { rqKeys } from '@/react-query-keys'

const REACT_QUERY_PERSIST_DB_NAME = 'era-react-query-cache'
const REACT_QUERY_PERSIST_STORE_NAME = 'react-query'
const reactQueryPersistStore = createStore(
  REACT_QUERY_PERSIST_DB_NAME,
  REACT_QUERY_PERSIST_STORE_NAME,
)

const isDataCloneError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const maybeError = error as { name?: string, message?: string }
  return maybeError.name === 'DataCloneError'
    || maybeError.message?.includes('could not be cloned') === true
}

const isUnknownObjectStoreError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const maybeError = error as { name?: string, message?: string }
  // Rely on DOMException name only to avoid locale-specific message matching.
  return maybeError.name === 'NotFoundError'
}

const toIdbSerializable = <T>(value: T): T => {
  const sanitize = (currentValue: unknown, path: WeakSet<object>): unknown => {
    if (currentValue === null) return null

    const valueType = typeof currentValue
    if (
      valueType === 'string'
      || valueType === 'number'
      || valueType === 'boolean'
    ) return currentValue

    if (valueType === 'bigint') return String(currentValue)
    if (valueType === 'function' || valueType === 'symbol' || valueType === 'undefined') return undefined

    if (currentValue instanceof Date) {
      const timestamp = currentValue.getTime()
      return Number.isNaN(timestamp) ? null : currentValue.toISOString()
    }
    if (currentValue instanceof RegExp) return currentValue.toString()
    if (currentValue instanceof Error) {
      return {
        name: currentValue.name,
        message: currentValue.message,
        stack: currentValue.stack,
      }
    }

    if (currentValue instanceof Map) {
      const mapEntries: unknown[] = []
      if (path.has(currentValue)) return '[Circular]'
      path.add(currentValue)
      for (const [ key, mapValue ] of currentValue.entries()) {
        mapEntries.push([ sanitize(key, path), sanitize(mapValue, path) ])
      }
      path.delete(currentValue)
      return mapEntries
    }

    if (currentValue instanceof Set) {
      const setValues: unknown[] = []
      if (path.has(currentValue)) return '[Circular]'
      path.add(currentValue)
      for (const item of currentValue.values()) {
        setValues.push(sanitize(item, path))
      }
      path.delete(currentValue)
      return setValues
    }

    if (ArrayBuffer.isView(currentValue)) {
      return Array.from(currentValue as unknown as ArrayLike<number>)
    }

    if (currentValue instanceof ArrayBuffer) {
      return Array.from(new Uint8Array(currentValue))
    }

    if (typeof currentValue === 'object') {
      if (path.has(currentValue)) return '[Circular]'

      if (Array.isArray(currentValue)) {
        const sanitizedArray: unknown[] = []
        path.add(currentValue)
        for (let i = 0; i < currentValue.length; i += 1) {
          const nextValue = sanitize(currentValue[i], path)
          sanitizedArray.push(nextValue === undefined ? null : nextValue)
        }
        path.delete(currentValue)
        return sanitizedArray
      }

      const sanitizedObject: Record<string, unknown> = {}
      path.add(currentValue)
      for (const key of Object.keys(currentValue)) {
        try {
          const nextValue = sanitize((currentValue as Record<string, unknown>)[key], path)
          if (nextValue !== undefined) sanitizedObject[key] = nextValue
        } catch {
          sanitizedObject[key] = '[Unserializable]'
        }
      }
      path.delete(currentValue)
      return sanitizedObject
    }

    return null
  }

  return sanitize(value, new WeakSet<object>()) as T
}

const MAX_OFFLINE_TRANSACTIONS_PAGES = 10

const isTransactionsInfiniteQueryKey = (queryKey: unknown): boolean => (
  Array.isArray(queryKey)
  && queryKey[0] === rqKeys.transactionsKey
  && queryKey[1] === 'all'
)

const trimTransactionsInfinitePagesForPersist = (client: PersistedClient): PersistedClient => {
  const queries = client?.clientState?.queries
  if (!Array.isArray(queries) || !queries.length) return client

  let changed = false
  const nextQueries = queries.map((query: any) => {
    if (!isTransactionsInfiniteQueryKey(query?.queryKey)) return query

    const data = query?.state?.data
    const pages = Array.isArray(data?.pages) ? data.pages : null
    if (!pages || pages.length <= MAX_OFFLINE_TRANSACTIONS_PAGES) return query

    changed = true
    return {
      ...query,
      state: {
        ...query.state,
        data: {
          ...data,
          pages: data.pages.slice(0, MAX_OFFLINE_TRANSACTIONS_PAGES),
          pageParams: Array.isArray(data?.pageParams)
            ? data.pageParams.slice(0, MAX_OFFLINE_TRANSACTIONS_PAGES)
            : data?.pageParams,
        },
      },
    }
  })

  if (!changed) return client

  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: nextQueries,
    },
  }
}

//
// Creates an IndexedDB persister for React Query
//
export const createIDBPersister = (
  idbValidKey: IDBValidKey = 'reactQuery',
): Persister => ({
  persistClient: async (client: PersistedClient) => {
    const trimmedClient = trimTransactionsInfinitePagesForPersist(client)
    try {
      await set(idbValidKey, trimmedClient, reactQueryPersistStore)
    } catch (error) {
      if (isDataCloneError(error)) {
        try {
          await set(idbValidKey, toIdbSerializable(trimmedClient), reactQueryPersistStore)
        } catch (secondError) {
          // Persist is best-effort. Do not break app runtime if IndexedDB is unavailable/misaligned.
          if (!isUnknownObjectStoreError(secondError)) throw secondError
        }
        return
      }

      // Persist is best-effort. Do not break app runtime if IndexedDB is unavailable/misaligned.
      if (!isUnknownObjectStoreError(error)) throw error
    }
  },
  restoreClient: async () => {
    try {
      return await get<PersistedClient>(idbValidKey, reactQueryPersistStore)
    } catch (error) {
      if (isUnknownObjectStoreError(error)) return undefined
      throw error
    }
  },
  removeClient: async () => {
    try {
      await del(idbValidKey, reactQueryPersistStore)
    } catch (error) {
      if (!isUnknownObjectStoreError(error)) throw error
    }
  },
})

// Explicit wipe of persisted React Query cache (used on logout).
export const clearIDBPersistedQueryClient = async (
  idbValidKey: IDBValidKey = 'reactQuery',
) => {
  try {
    await del(idbValidKey, reactQueryPersistStore)
  } catch (error) {
    if (!isUnknownObjectStoreError(error)) throw error
  }
}
